import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { PROJECTS } from '../lib/site';

/**
 * Proyectos destacados — grid editorial asimétrico con fotos reales.
 * Primer proyecto grande (2x2), luego columna derecha y fila de tres.
 */
const ProjectShowcase = () => {
  const [main, rightA, rightB, ...rest] = PROJECTS;
  const rowThree = rest.slice(0, 3);

  const Card = ({ p, size }: { p: (typeof PROJECTS)[0]; size: 'lg' | 'md' | 'sm' }) => (
    <Link
      to="/galeria"
      className={`project-card group block reveal ${size === 'lg' ? 'md:col-span-2 md:row-span-2' : ''}`}
      aria-label={`${p.title} en ${p.location} (${p.year})`}
    >
      <div className={`relative overflow-hidden ${size === 'lg' ? 'aspect-square md:aspect-auto md:h-full min-h-[420px]' : size === 'md' ? 'aspect-[4/5]' : 'aspect-[4/5]'}`}>
        <img
          src={p.image}
          alt={`${p.title} — ${p.type} realizada por HGO Servicios en ${p.location}, ${p.year}`}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/55 via-ink/5 to-transparent opacity-80 group-hover:opacity-95 transition-opacity duration-300" />

        {/* Número de proyecto */}
        <span className="absolute top-4 left-4 text-paper/90 font-display font-black text-sm tracking-[0.18em] bg-ink/45 backdrop-blur-sm px-3 py-1.5 rounded-[3px]">
          {p.id}
        </span>

        {/* Info */}
        <div className="absolute bottom-0 inset-x-0 p-5 translate-y-1 group-hover:translate-y-0 transition-transform duration-[400ms]" style={{ transitionTimingFunction: 'cubic-bezier(0.23,1,0.32,1)' }}>
          <p className="text-[0.62rem] font-bold tracking-[0.22em] uppercase text-paper/75">
            {p.type} · {p.location} · {p.year}
          </p>
          <div className="flex items-end justify-between gap-3 mt-1.5">
            <h3 className={`text-paper font-display font-bold leading-tight ${size === 'lg' ? 'text-2xl md:text-3xl' : 'text-lg'}`}>
              {p.title}
            </h3>
            <span className={`grid place-items-center rounded-full bg-paper text-ink shrink-0 transition-all duration-[250ms] ${size === 'lg' ? 'w-11 h-11' : 'w-9 h-9 opacity-0 group-hover:opacity-100'}`} style={{ transitionTimingFunction: 'cubic-bezier(0.23,1,0.32,1)' }}>
              <ArrowUpRight size={18} className="transition-transform duration-[250ms] group-hover:rotate-45" />
            </span>
          </div>
          {size === 'lg' && <p className="text-paper/80 text-sm mt-2 max-w-md hidden md:block">{p.description}</p>}
        </div>
      </div>
    </Link>
  );

  return (
    <section id="proyectos" className="bg-paper-2 border-y border-line py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14 reveal">
          <div>
            <p className="label">Obra reciente</p>
            <h2 className="font-display font-black uppercase tracking-[-0.02em] text-section-title mt-5">
              Proyectos <em className="font-serifit normal-case font-normal text-clay">entregados</em>
            </h2>
          </div>
          <Link to="/galeria" className="btn border border-ink text-ink group">
            Ver toda la galería
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 md:grid-rows-2 gap-5 md:gap-6">
          <Card p={main} size="lg" />
          <Card p={rightA} size="md" />
          <Card p={rightB} size="md" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mt-5 md:mt-6">
          {rowThree.map((p) => (
            <Card key={p.id} p={p} size="sm" />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProjectShowcase;
